import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../utils/toast';
import { ArrowLeft, FileText, CheckCircle2, Hourglass, CircleDollarSign } from 'lucide-react';
import useCobroStore from '../../../store/cobro';
import { Button } from '../../../components/ui/Button';
import { CopyButton } from '../../../components/ui/CopyButton';
import { Skeleton } from '../../../components/ui/Skeleton';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { useIsMobile } from '../../../hooks/useIsMobile';
import PaymentStatusBadge from '../../adminView/cobro/components/PaymentStatusBadge';
import { NoteDueDateLine } from '../../adminView/cobro/components/DueDateInfo';
import {
  formatCurrency,
  formatDate,
  CHARGE_MODE_LABELS,
} from '../../adminView/cobro/utils/cobroHelpers';
import {
  amountDueOf,
  paidAmountOf,
  paidReferencesOf,
  paymentMethodLabelKey,
} from '../../adminView/cobro/utils/payments';

// Historial del comercio: Fecha · Monto · Método · Referencia · Estado
const PAYMENT_COLS =
  'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 0.8fr)';

const SummaryCard = ({ label, value, accent }) => (
  <div className={`bill-summary__card${accent ? ' bill-summary__card--accent' : ''}`}>
    <div className="bill-summary__label">{label}</div>
    <div className={`bill-summary__value${accent ? ' bill-summary__value--accent' : ''}`}>
      {value}
    </div>
  </div>
);

// Columnas del detalle de líneas: # · Descripción · Fecha · Valor
const LINE_COLS = '1.4fr 1.8fr 1fr 1fr';

const PagosNoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const { getMyNote } = useCobroStore((s) => ({
    getMyNote: s.getMyNote,
  }));

  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);

  // Se carga por id (no desde el listado en memoria) para soportar refresh y
  // enlace directo a la URL. El backend valida que la nota sea del comercio.
  useEffect(() => {
    let active = true;
    setLoading(true);
    getMyNote(id)
      .then((data) => {
        if (active) setNote(data);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message ?? t('No se pudo cargar la nota de cobro.'));
        navigate('/home/pagos');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, getMyNote, navigate, t]);

  const lineItems = useMemo(() => note?.lineItems ?? [], [note]);

  // Lleva a la vista de selección de método (no inicia el cargo directo).
  const handlePay = () => navigate(`/home/pagos/${note._id}/pagar`);

  if (loading || !note) {
    return (
      <div className="bill-page" role="status" aria-live="polite">
        <Skeleton className="h-4 w-32" style={{ marginBottom: '1rem' }} />
        <div className="bill-card bill-card--pad">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" style={{ marginTop: '0.5rem' }} />
        </div>
        <div className="bill-summary">
          {[0, 1, 2, 3].map((i) => (
            <div className="bill-summary__card" key={i}>
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-6 w-24" style={{ marginTop: '0.5rem' }} />
            </div>
          ))}
        </div>
        <div className="bill-card bill-card--clip" style={{ marginTop: '1rem' }}>
          <TableSkeleton rows={8} columns={4} className="border-0" />
        </div>
      </div>
    );
  }

  const n = note;
  const cur = n.currency ?? 'CLP';
  // Desglose de IVA según el tratamiento del contrato (taxMode) persistido en la nota.
  const hasTax = n.taxAmount > 0;
  const isInclusive = n.taxMode === 'inclusive';
  const taxRatePct = Math.round((n.taxRate || 0) * 100);
  const taxLabel = taxRatePct > 0 ? t('IVA ({{pct}}%)', { pct: taxRatePct }) : t('Impuesto');
  const netAmount = Math.round((n.totalAmount || 0) - (n.taxAmount || 0));
  const isCancelled = n.status === 'cancelled' || n.paymentStatus === 'cancelled';
  const isPaid = n.paymentStatus === 'paid';
  const isInReview = n.paymentStatus === 'in_review';
  const isPartiallyPaid = n.paymentStatus === 'partially_paid';
  const canPay = !isPaid && !isCancelled && !isInReview;
  const dateHeader = n.chargeMode === 'entregado' ? t('Fecha entrega') : t('Fecha recolección');
  const payments = n.payments ?? [];
  const amountPaid = paidAmountOf(n);
  const amountDue = amountDueOf(n);
  const paidRefs = paidReferencesOf(n);

  return (
    <div className="bill bill-page" style={{ padding: 16 }}>
      <button type="button" className="bill-back" onClick={() => navigate('/home/pagos')}>
        <ArrowLeft className="w-[18px] h-[18px]" />
        {t('Volver a pagos')}
      </button>

      {/* Cabecera */}
      <div className="bill-card bill-card--pad">
        <div className="bill-doc-head">
          <div>
            <div className="bill-doc-head__title">
              <h2>{t('Nota de cobro #')}{n.folio ?? '—'}</h2>
              <CopyButton value={n.folio} label={t('Copiar folio')} copiedMessage={t('Folio copiado')} />
              <PaymentStatusBadge note={n} withIcon />
            </div>
            <div className="bill-doc-head__meta">
              {t(CHARGE_MODE_LABELS[n.chargeMode] ?? n.chargeMode)} · {t('Periodo:')}{' '}
              <strong>{formatDate(n.periodStart)}</strong> → <strong>{formatDate(n.periodEnd)}</strong>
            </div>
            <NoteDueDateLine note={n} className="mt-1" />
          </div>

          <div className="bill-doc-head__actions">
            {n.pdfUrl && (
              <Button
                size="sm"
                variant="outline"
                iconLeft={<FileText className="w-4 h-4" />}
                onClick={() => window.open(n.pdfUrl, '_blank', 'noopener')}
              >
                {t('Descargar PDF')}
              </Button>
            )}
            {canPay && (
              <Button size="sm" variant="primary" onClick={handlePay}>
                {isPartiallyPaid ? t('Pagar saldo') : t('Pagar')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Banner de pago */}
      {isPaid && (
        <div className="bill-connbanner bill-connbanner--connected">
          <span className="bill-connbanner__icon"><CheckCircle2 className="w-5 h-5" /></span>
          <div className="bill-connbanner__body">
            <span className="bill-connbanner__title">{t('Nota pagada')}</span>
            <span className="bill-connbanner__msg">
              {n.paidAt ? t('Pagada el {{date}}', { date: formatDate(n.paidAt) }) : t('Esta nota ya fue cobrada.')}
              {paidRefs.length > 0 && ` · ${t('Ref')} ${paidRefs.join(' · ')}`}
            </span>
          </div>
        </div>
      )}
      {isPartiallyPaid && (
        <div className="bill-connbanner bill-connbanner--idle">
          <span className="bill-connbanner__icon"><CircleDollarSign className="w-5 h-5" /></span>
          <div className="bill-connbanner__body">
            <span className="bill-connbanner__title">{t('Pago parcial registrado')}</span>
            <span className="bill-connbanner__msg">
              {t('Pagaste {{paid}}. Saldo pendiente: {{due}}.', {
                paid: formatCurrency(amountPaid, cur),
                due: formatCurrency(amountDue, cur),
              })}
            </span>
          </div>
        </div>
      )}
      {isInReview && (
        <div className="bill-connbanner bill-connbanner--idle">
          <span className="bill-connbanner__icon"><Hourglass className="w-5 h-5" /></span>
          <div className="bill-connbanner__body">
            <span className="bill-connbanner__title">{t('Pago en revisión')}</span>
            <span className="bill-connbanner__msg">
              {t('Informaste una transferencia. Tu operador la revisará y confirmará el pago.')}
            </span>
          </div>
        </div>
      )}

      {/* Resumen */}
      <div className="bill-summary">
        <SummaryCard label={t('Líneas')} value={lineItems.length} />
        <SummaryCard label={t('Subtotal')} value={formatCurrency(n.subtotal, cur)} />
        <SummaryCard
          label={isInclusive && hasTax ? t('{{label}} incl.', { label: taxLabel }) : taxLabel}
          value={formatCurrency(n.taxAmount, cur)}
        />
        <SummaryCard label={t('Total')} value={formatCurrency(n.totalAmount, cur)} accent />
        <SummaryCard label={t('Pagado')} value={formatCurrency(amountPaid, cur)} />
        <SummaryCard
          label={t('Saldo')}
          value={formatCurrency(amountDue, cur)}
          accent={amountDue > 0}
        />
      </div>

      {/* Historial de pagos de la nota. El comercio no recibe datos internos
          (quién lo registró, comentarios ni el motivo de una anulación). */}
      {payments.length > 0 && (
        <div className="bill-card bill-card--clip" style={{ marginTop: '1rem' }}>
          <div className="bill-card__head">
            <h3 className="bill-card__title">
              {t('Historial de pagos')} <span>({payments.length})</span>
            </h3>
          </div>
          {isMobile ? (
            <div className="bill-cards">
              {payments.map((payment) => (
                <div
                  key={payment._id}
                  className={`bill-card-item${payment.voidedAt ? ' opacity-60' : ''}`}
                >
                  <div className="bill-card-item__head">
                    <span className={`bill-card-item__title${payment.voidedAt ? ' line-through' : ''}`}>
                      {formatDate(payment.paidAt)}
                    </span>
                    <span className={`bill-td-amount${payment.voidedAt ? ' line-through' : ''}`}>
                      {formatCurrency(payment.amount, payment.currency ?? cur)}
                    </span>
                  </div>
                  <div className="bill-card-item__rows">
                    <div className="bill-card-item__field">
                      <span className="bill-card-item__k">{t('Método')}</span>
                      <span className="bill-card-item__v">{t(paymentMethodLabelKey(payment.method))}</span>
                    </div>
                    <div className="bill-card-item__field">
                      <span className="bill-card-item__k">{t('Referencia')}</span>
                      <span className="bill-card-item__v bill-td-muted">{payment.reference || '—'}</span>
                    </div>
                  </div>
                  {payment.voidedAt && (
                    <span className="bill-badge bill-badge--neutral bill-badge--sm mt-2">
                      {t('Anulado')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bill-grid" role="table" style={{ '--bill-cols': PAYMENT_COLS }}>
              <div className="bill-grid__head" role="row">
                <span className="bill-grid__hcell" role="columnheader">{t('Fecha de pago')}</span>
                <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Monto')}</span>
                <span className="bill-grid__hcell" role="columnheader">{t('Método')}</span>
                <span className="bill-grid__hcell" role="columnheader">{t('Referencia')}</span>
                <span className="bill-grid__hcell" role="columnheader">{t('Estado')}</span>
              </div>
              {payments.map((payment) => (
                <div
                  key={payment._id}
                  className={`bill-grid__row${payment.voidedAt ? ' opacity-60' : ''}`}
                  role="row"
                >
                  <span className={`bill-td-muted${payment.voidedAt ? ' line-through' : ''}`} role="cell">
                    {formatDate(payment.paidAt)}
                  </span>
                  <span
                    className={`bill-grid__cell--end bill-td-amount${payment.voidedAt ? ' line-through' : ''}`}
                    role="cell"
                  >
                    {formatCurrency(payment.amount, payment.currency ?? cur)}
                  </span>
                  <span role="cell">{t(paymentMethodLabelKey(payment.method))}</span>
                  <span className="bill-td-muted" role="cell">{payment.reference || '—'}</span>
                  <span role="cell">
                    {payment.voidedAt ? (
                      <span className="bill-badge bill-badge--neutral bill-badge--sm">{t('Anulado')}</span>
                    ) : (
                      <span className="bill-badge bill-badge--success bill-badge--sm">{t('Vigente')}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Detalle de líneas */}
      <div className="bill-card bill-card--clip">
        <div className="bill-card__head">
          <h3 className="bill-card__title">
            {t('Detalle de líneas')} <span>({lineItems.length})</span>
          </h3>
        </div>

        {isMobile ? (
          <div className="bill-cards">
            {lineItems.map((li, i) => (
              <div key={i} className="bill-card-item">
                <div className="bill-card-item__head">
                  <span className="bill-card-item__title bill-td-track">#{li.followingNumber} <CopyButton value={li.followingNumber} label={t('Copiar número de seguimiento')} copiedMessage={t('Número de seguimiento copiado')} /></span>
                  <span className="bill-td-amount">{formatCurrency(li.totalAmount, cur)}</span>
                </div>
                <div className="bill-card-item__rows">
                  <div className="bill-card-item__field bill-card-item__field--full">
                    <span className="bill-card-item__k">{t('Descripción')}</span>
                    <span className="bill-card-item__v bill-td-muted">{li.description}</span>
                  </div>
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{dateHeader}</span>
                    <span className="bill-card-item__v bill-td-sub">{formatDate(li.deliveredAt)}</span>
                  </div>
                </div>
              </div>
            ))}
            {lineItems.length === 0 && (
              <div className="bill-empty" style={{ padding: '1.5rem' }}>{t('Esta nota no tiene líneas.')}</div>
            )}
          </div>
        ) : (
          <div className="bill-grid" role="table" style={{ '--bill-cols': LINE_COLS }}>
            <div className="bill-grid__head" role="row">
              <span className="bill-grid__hcell" role="columnheader">#</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Descripción')}</span>
              <span className="bill-grid__hcell" role="columnheader">{dateHeader}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Valor')}</span>
            </div>
            {lineItems.map((li, i) => (
              <div key={i} className="bill-grid__row" role="row">
                <span className="bill-td-track" role="cell">{li.followingNumber} <CopyButton value={li.followingNumber} label={t('Copiar número de seguimiento')} copiedMessage={t('Número de seguimiento copiado')} /></span>
                <span className="bill-td-muted" role="cell">{li.description}</span>
                <span className="bill-td-sub" role="cell">{formatDate(li.deliveredAt)}</span>
                <span className="bill-grid__cell--end bill-td-amount" role="cell">
                  {formatCurrency(li.totalAmount, cur)}
                </span>
              </div>
            ))}
            {lineItems.length === 0 && (
              <div className="bill-empty" style={{ padding: '1.5rem' }}>{t('Esta nota no tiene líneas.')}</div>
            )}
          </div>
        )}

        {/* Totales */}
        <div className="bill-pagination" style={{ justifyContent: 'flex-end', gap: '2rem', flexWrap: 'wrap' }}>
          <span><span className="bill-td-sub me-2">{t('Subtotal')}</span><span className="bill-td-amount">{formatCurrency(n.subtotal, cur)}</span></span>
          {/* exclusive: el IVA se muestra antes del Total (base neta). */}
          {hasTax && !isInclusive && (
            <span><span className="bill-td-sub me-2">{taxLabel}</span><span className="bill-td-amount">{formatCurrency(n.taxAmount, cur)}</span></span>
          )}
          <span><span className="bill-td-strong me-2">{t('Total')}</span><span className="bill-td-amount" style={{ fontSize: '1rem' }}>{formatCurrency(n.totalAmount, cur)}</span></span>
          {/* inclusive: el Total no cambia; se desglosa el neto y el IVA incluido. */}
          {hasTax && isInclusive && (
            <>
              <span><span className="bill-td-sub me-2">{t('Neto')}</span><span className="bill-td-amount">{formatCurrency(netAmount, cur)}</span></span>
              <span><span className="bill-td-sub me-2">{t('{{label}} incluido', { label: taxLabel })}</span><span className="bill-td-amount">{formatCurrency(n.taxAmount, cur)}</span></span>
            </>
          )}
        </div>
        {hasTax && isInclusive && (
          <p className="bill-hint" style={{ textAlign: 'right', padding: '0 1.25rem 0.75rem' }}>
            {t('Los valores de las tarifas incluyen IVA.')}
          </p>
        )}
      </div>
    </div>
  );
};

export default PagosNoteDetail;
